import { Component, HostListener } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { Router, RouterOutlet } from '@angular/router';
import { MainSideMenuComponent } from './components/main-side-menu/main-side-menu.component';
import { StateService } from './services/state.service';
import { NgClass, NgIf } from '@angular/common';
import { AuthService } from '@auth0/auth0-angular';
import { combineLatest, distinctUntilChanged, filter, map, Observable, Subject, throttleTime } from 'rxjs';
import { AuthorizationService } from './services/authorization.service';
import { PosthogService } from './services/posthog.service';
import { UserProfile } from './shared/types/UserProfile';
import { CoralogixRUMService } from './services/coralogix-rum.service';
import { PolarisRoutePaths, RoutePaths } from './shared/types/common';
import { AssetVersionPipe } from './pipes/asset-version.pipe';
import { FreeTrialBannerComponent } from './shared/components/free-trial-banner/free-trial-banner.component';
import { NotificationComponent } from './shared/components/notification/notification.component';
import { SMALL_SCREENS_NOTIFICATION } from './utils/constants';
import { BreakpointObserver } from '@angular/cdk/layout';
import { AsyncPipe } from '@angular/common';
import {PolarisRuntimeBootstrapService} from './services/polaris/polaris-runtime-bootstrap.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, MainSideMenuComponent, NgIf, NgClass, AssetVersionPipe, FreeTrialBannerComponent, NotificationComponent, AsyncPipe],
  standalone: true,
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  animations: [
    trigger('spinnerFade', [
      transition(':leave', [
        animate('400ms ease-in', style({ opacity: 0 }))
      ])
    ])
  ]
})
export class AppComponent {
  private sideMenuRoutes = [
    ...Object.values(RoutePaths).filter(
      (path) => path !== RoutePaths.WORKSPACE_ERROR && path !== RoutePaths.EMPTY && path !== RoutePaths.TRIAL,
    ),
    ...Object.values(PolarisRoutePaths),
  ] as string[];

  private publicRoutes = [RoutePaths.TRIAL] as string[];

  title = 'brand-intelligence-console-client';
  public loading = true;
  private scroll$ = new Subject<number>();
  public showSmallScreensNotification$: Observable<boolean>;
  public SMALL_SCREENS_NOTIFICATION = SMALL_SCREENS_NOTIFICATION;

  showTrialBanner: boolean = false;
  bannerDaysLeft: number = 0;
  isExpiredUser: boolean = false;

  constructor(
    private breakpointObserver: BreakpointObserver,
    private stateService: StateService,
    private auth: AuthService,
    private authorizationService: AuthorizationService,
    private router: Router,
    private postHogService: PosthogService,
    private coralogixRUMService: CoralogixRUMService,
    private polarisRuntimeBootstrapService: PolarisRuntimeBootstrapService
  ) {
    this.polarisRuntimeBootstrapService.start();
    this.subscribeToLoading();
    this.subscribeToUserProfile();
    this.scroll$
      .pipe(throttleTime(100, undefined, { leading: true, trailing: true }))
      .subscribe(scrollTop => {
        this.stateService.isRouterOutletScrolledToTop.set(scrollTop === 0);
      });
    this.showSmallScreensNotification$ = this.breakpointObserver
      .observe(['(max-width: 1023px)'])
      .pipe(
        map(result => result.matches),
        distinctUntilChanged()
      );
  }

  @HostListener('scroll', ['$event'])
  onScroll(event: Event) {
    const element = event.target as HTMLElement;
    this.scroll$.next(element.scrollTop);
  }

  subscribeToLoading(): void {
    combineLatest([
      this.stateService.loading$,
      this.auth.isAuthenticated$
    ])
      .pipe(
        map(([stateLoading, isAuthenticated]) => {
          if (this.isPublicRoute) {
            return stateLoading;
          }
          return stateLoading || !isAuthenticated;
        })
      )
      .subscribe((combinedLoading) => {
        this.loading = combinedLoading;
      });
  }

  get isPublicRoute(): boolean {
    return this.publicRoutes.includes(this.currentRoute.slice(1) as RoutePaths);
  }

  subscribeToUserProfile(): void {
    this.authorizationService.userProfile$
      .pipe(
        filter(() => this.authorizationService.profileInitiated),
      )
      .subscribe(
        {
          next: (profile) => {
            this.coralogixRUMService.identify(profile);
            this.postHogService.identify(profile);
            this.initializePendo(profile);
            this.initializeTrialBannerState();
          },
          error: () => {
          }
        }
      );
  }

  private initializeTrialBannerState(): void {
    this.isExpiredUser = this.authorizationService.isExpiredUser;
    const isTrialOrExpired = this.authorizationService.isTrialUser || this.isExpiredUser;

    if (!isTrialOrExpired) {
      return;
    }

    const daysLeft = this.authorizationService.userProfile.trialDaysLeft;
    if (typeof daysLeft === 'number') {
      this.showTrialBanner = true;
      this.bannerDaysLeft = daysLeft;
    }
  }

  private initializePendo(profile: UserProfile): void {
    (window as any).pendo.initialize({
      visitor: {
        id: profile.email,
        name: `${profile.firstName} ${profile.lastName}`,
        role: profile.role || "NA",
      },
      account: {
        id: profile.email
      }
    });
  }

  get currentRoute(): string {
    return this.router.url.split('?')[0];
  }

  get shouldListenScroll(): boolean {
    const routePath = this.currentRoute.slice(1);
    // Includes child routes as well.
    return [RoutePaths.CAMPAIGNS, RoutePaths.MY_BRAND, RoutePaths.COMPETITION, RoutePaths.MESSAGING].some(path =>
      routePath === path || routePath.startsWith(path + '/')
    );
  }

  get showSideMenu(): boolean {
    return this.sideMenuRoutes.includes(this.currentRoute.slice(1).split('/')[0] as RoutePaths);
  }

}
